import { isAxiosError } from "axios";

/** The trimmed string in `value`, or `undefined` if there isn't one. */
function nonBlank(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

/** `value` decoded as a JSON array, or `undefined` if it is neither JSON nor an array. */
function parseJsonArray(value: string): unknown[] | undefined {
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The joined messages of a zod issue array, keeping only `custom` issues.
 *
 * A `custom` issue carries a message someone wrote for this schema, which is
 * therefore addressed to whoever sent the request. Every other code is zod's
 * own diagnostic — `Unrecognized key: "agenticUi"` during a deploy window,
 * `Invalid input: expected string, received undefined` — and describes a client
 * bug the user can do nothing about. Showing those would be worse than the
 * generic prompt they replace, so they yield nothing and the caller falls back.
 */
function zodIssueMessages(issues: unknown[]): string | undefined {
  const messages = issues
    .map((issue) => {
      const { code, message } = (issue ?? {}) as { code?: unknown; message?: unknown };
      return code === "custom" ? nonBlank(message) : undefined;
    })
    .filter((message) => message !== undefined);
  return messages.length > 0 ? messages.join(" ") : undefined;
}

/**
 * The server's own explanation for a failed request, when it gave one.
 *
 * Four response shapes reach us, listed in the order the branches below test
 * for them:
 *
 *   `HTTPException`  a bare `text/plain` body (`err.getResponse()` in `app.ts`)
 *   `{ message }`    a direct `c.json({ message }, status)`
 *   `{ error }`      a direct `c.json({ error }, status)` — much the most common
 *   `zValidator`     `{ success: false, error: { name: "ZodError", message } }`
 *
 * The last is the awkward one, and the reason this can't just read a field. zod
 * 4 declares `ZodError.issues` non-enumerable, so `JSON.stringify` drops it and
 * the issue array survives only as JSON encoded *inside* `message`; reading
 * `error.issues` off the wire finds nothing at all.
 *
 * Only 4xx bodies are read. A 5xx has no user-actionable reason by definition,
 * and its body is often not ours at all — a proxy or load balancer answers a 502
 * with an HTML error page, which axios leaves as a string and which would
 * otherwise be rendered verbatim. That case wants the caller's generic wording.
 *
 * A network failure carries no server response and yields `undefined`, so
 * callers keep their own wording for when there is genuinely nothing to report.
 *
 * Worth the shape-sniffing: a reason dropped here becomes a generic retry
 * prompt, which tells the user nothing they can act on (BIG-7284).
 */
export function apiErrorMessage(error: unknown): string | undefined {
  const data = apiErrorBody(error);
  if (typeof data === "string") return nonBlank(data);
  if (typeof data !== "object" || data === null) return undefined;

  const { message, error: nested } = data as { message?: unknown; error?: unknown };
  const direct = nonBlank(message) ?? nonBlank(nested);
  if (direct !== undefined) return direct;
  if (typeof nested !== "object" || nested === null) return undefined;

  // An array payload IS the issue list, so report its messages or nothing —
  // falling back to the raw string would surface a literal "[]" as the reason.
  const nestedMessage = (nested as { message?: unknown }).message;
  if (typeof nestedMessage !== "string") return undefined;
  const issues = parseJsonArray(nestedMessage);
  return issues === undefined ? nonBlank(nestedMessage) : zodIssueMessages(issues);
}

/**
 * The structured `reason` a 4xx carries, when the server sent one. Reason
 * constants live in the shared types (`@/types/billing.ts`,
 * `@/types/entitlements.ts`); callers compare against those rather than
 * literals so the wire strings cannot drift.
 */
export function apiErrorReason(error: unknown): string | undefined {
  const data = apiErrorBody(error);
  if (typeof data !== "object" || data === null) return undefined;
  const { reason } = data as { reason?: unknown };
  return typeof reason === "string" ? nonBlank(reason) : undefined;
}

/**
 * What a log line may say about a failed request: the diagnostics, not the
 * request. An `AxiosError` carries the request body on `config.data`, and on
 * the web `console.*` is forwarded to Sentry and Statsig, so logging the error
 * itself from a credential path would hand a live one-time code to two vendors
 * (CASA 6.5.1). A non-axios error carries no request and is returned as is.
 */
export function loggableRequestError(error: unknown): unknown {
  if (!isAxiosError(error)) return error;
  return {
    name: error.name,
    message: error.message,
    code: error.code,
    status: error.response?.status,
    serverMessage: apiErrorMessage(error),
  };
}

/**
 * The response body of a 4xx axios rejection, or undefined for anything else.
 * The shared gate for `apiErrorMessage`/`apiErrorReason`: only 4xx bodies are
 * ours to interpret (a 5xx is often a proxy's HTML page), and a network
 * failure has no body at all.
 */
function apiErrorBody(error: unknown): unknown {
  if (!isAxiosError(error)) return undefined;
  const status = error.response?.status;
  if (status === undefined || status < 400 || status >= 500) return undefined;
  return error.response?.data;
}
